import * as React from 'react';
import { cn } from '../../lib/utils';

// ============================================
// Container - Centered container with max-width
// ============================================

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Max width: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' */
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
    /** Center content horizontally */
    center?: boolean;
}

const containerSizes = {
    sm: 'max-w-screen-sm',
    md: 'max-w-screen-md',
    lg: 'max-w-screen-lg',
    xl: 'max-w-screen-xl',
    '2xl': 'max-w-screen-2xl',
    full: 'max-w-full',
};

const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
    ({ className, size = 'lg', center = true, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                containerSizes[size],
                center && 'mx-auto',
                'w-full px-4 sm:px-6 lg:px-8',
                className
            )}
            {...props}
        />
    )
);
Container.displayName = 'Container';

// ============================================
// Stack - Vertical flex layout
// ============================================

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Gap between items: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' */
    gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    /** Alignment: 'start' | 'center' | 'end' | 'stretch' */
    align?: 'start' | 'center' | 'end' | 'stretch';
    /** Justify: 'start' | 'center' | 'end' | 'between' | 'around' */
    justify?: 'start' | 'center' | 'end' | 'between' | 'around';
    /** Whether to wrap items */
    wrap?: boolean;
}

const gapClasses = {
    none: 'gap-0',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
    '2xl': 'gap-12',
};

const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
};

const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
};

const Stack = React.forwardRef<HTMLDivElement, StackProps>(
    (
        { className, gap = 'md', align = 'stretch', justify = 'start', wrap = false, ...props },
        ref
    ) => (
        <div
            ref={ref}
            className={cn(
                'flex flex-col',
                gapClasses[gap],
                alignClasses[align],
                justifyClasses[justify],
                wrap && 'flex-wrap',
                className
            )}
            {...props}
        />
    )
);
Stack.displayName = 'Stack';

// ============================================
// Row - Horizontal flex layout
// ============================================

export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Gap between items */
    gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    /** Alignment */
    align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
    /** Justify */
    justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
    /** Whether to wrap items */
    wrap?: boolean;
    /** Reverse direction */
    reverse?: boolean;
}

const alignRowClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
    baseline: 'items-baseline',
};

const justifyRowClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
};

const Row = React.forwardRef<HTMLDivElement, RowProps>(
    (
        {
            className,
            gap = 'md',
            align = 'center',
            justify = 'start',
            wrap = false,
            reverse = false,
            ...props
        },
        ref
    ) => (
        <div
            ref={ref}
            className={cn(
                'flex',
                reverse ? 'flex-row-reverse' : 'flex-row',
                gapClasses[gap],
                alignRowClasses[align],
                justifyRowClasses[justify],
                wrap && 'flex-wrap',
                className
            )}
            {...props}
        />
    )
);
Row.displayName = 'Row';

// ============================================
// Grid - CSS Grid layout
// ============================================

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Number of columns: 1-12 or 'auto' */
    cols?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'auto';
    /** Responsive columns: { sm?: cols, md?: cols, lg?: cols, xl?: cols } */
    responsive?: {
        sm?: 1 | 2 | 3 | 4 | 5 | 6;
        md?: 1 | 2 | 3 | 4 | 5 | 6;
        lg?: 1 | 2 | 3 | 4 | 5 | 6;
        xl?: 1 | 2 | 3 | 4 | 5 | 6;
    };
    /** Gap between items */
    gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    /** Alignment of grid items */
    align?: 'start' | 'center' | 'end' | 'stretch';
    /** Justify grid items */
    justify?: 'start' | 'center' | 'end' | 'stretch';
}

const gridColsClasses: Record<string | number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
    9: 'grid-cols-9',
    10: 'grid-cols-10',
    11: 'grid-cols-11',
    12: 'grid-cols-12',
    auto: 'grid-cols-[repeat(auto-fit,minmax(250px,1fr))]',
};

const responsiveColsClasses = {
    sm: {
        1: 'sm:grid-cols-1',
        2: 'sm:grid-cols-2',
        3: 'sm:grid-cols-3',
        4: 'sm:grid-cols-4',
        5: 'sm:grid-cols-5',
        6: 'sm:grid-cols-6',
    },
    md: {
        1: 'md:grid-cols-1',
        2: 'md:grid-cols-2',
        3: 'md:grid-cols-3',
        4: 'md:grid-cols-4',
        5: 'md:grid-cols-5',
        6: 'md:grid-cols-6',
    },
    lg: {
        1: 'lg:grid-cols-1',
        2: 'lg:grid-cols-2',
        3: 'lg:grid-cols-3',
        4: 'lg:grid-cols-4',
        5: 'lg:grid-cols-5',
        6: 'lg:grid-cols-6',
    },
    xl: {
        1: 'xl:grid-cols-1',
        2: 'xl:grid-cols-2',
        3: 'xl:grid-cols-3',
        4: 'xl:grid-cols-4',
        5: 'xl:grid-cols-5',
        6: 'xl:grid-cols-6',
    },
};

const gridAlignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
};

const gridJustifyClasses = {
    start: 'justify-items-start',
    center: 'justify-items-center',
    end: 'justify-items-end',
    stretch: 'justify-items-stretch',
};

const Grid = React.forwardRef<HTMLDivElement, GridProps>(
    (
        {
            className,
            cols = 1,
            responsive,
            gap = 'md',
            align = 'stretch',
            justify = 'stretch',
            ...props
        },
        ref
    ) => {
        const responsiveClasses = responsive
            ? Object.entries(responsive)
                  .filter(([_, value]) => value !== undefined)
                  .map(
                      ([breakpoint, value]) =>
                          responsiveColsClasses[breakpoint as keyof typeof responsiveColsClasses][
                              value as keyof (typeof responsiveColsClasses)['sm']
                          ]
                  )
                  .join(' ')
            : '';

        return (
            <div
                ref={ref}
                className={cn(
                    'grid',
                    gridColsClasses[cols],
                    responsiveClasses,
                    gapClasses[gap],
                    gridAlignClasses[align],
                    gridJustifyClasses[justify],
                    className
                )}
                {...props}
            />
        );
    }
);
Grid.displayName = 'Grid';

// ============================================
// GridItem - Grid item with span control
// ============================================

export interface GridItemProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Column span: 1-12 or 'full' */
    colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'full';
    /** Row span */
    rowSpan?: 1 | 2 | 3 | 4 | 5 | 6;
}

const colSpanClasses: Record<string | number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
    full: 'col-span-full',
};

const rowSpanClasses: Record<number, string> = {
    1: 'row-span-1',
    2: 'row-span-2',
    3: 'row-span-3',
    4: 'row-span-4',
    5: 'row-span-5',
    6: 'row-span-6',
};

const GridItem = React.forwardRef<HTMLDivElement, GridItemProps>(
    ({ className, colSpan, rowSpan, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                colSpan && colSpanClasses[colSpan],
                rowSpan && rowSpanClasses[rowSpan],
                className
            )}
            {...props}
        />
    )
);
GridItem.displayName = 'GridItem';

// ============================================
// Spacer - Flexible space component
// ============================================

export interface SpacerProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Fixed size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    /** Grow to fill available space */
    grow?: boolean;
}

const spacerSizes = {
    xs: 'h-1 w-1',
    sm: 'h-2 w-2',
    md: 'h-4 w-4',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-12 w-12',
};

const Spacer = React.forwardRef<HTMLDivElement, SpacerProps>(
    ({ className, size, grow = false, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(grow ? 'flex-1' : size && spacerSizes[size], className)}
            {...props}
        />
    )
);
Spacer.displayName = 'Spacer';

// ============================================
// Divider - Horizontal or vertical divider
// ============================================

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Orientation */
    orientation?: 'horizontal' | 'vertical';
    /** Decorative (removes semantic meaning) */
    decorative?: boolean;
}

const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
    ({ className, orientation = 'horizontal', decorative = true, ...props }, ref) => (
        <div
            ref={ref}
            role={decorative ? 'none' : 'separator'}
            aria-orientation={decorative ? undefined : orientation}
            className={cn(
                'shrink-0 bg-border',
                orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
                className
            )}
            {...props}
        />
    )
);
Divider.displayName = 'Divider';

// ============================================
// Center - Center content both horizontally and vertically
// ============================================

export interface CenterProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Only center horizontally */
    horizontal?: boolean;
    /** Only center vertically */
    vertical?: boolean;
    /** Use inline-flex instead of flex */
    inline?: boolean;
}

const Center = React.forwardRef<HTMLDivElement, CenterProps>(
    ({ className, horizontal, vertical, inline = false, ...props }, ref) => {
        const justifyClass = !vertical || horizontal ? 'justify-center' : '';
        const alignClass = !horizontal || vertical ? 'items-center' : '';

        return (
            <div
                ref={ref}
                className={cn(inline ? 'inline-flex' : 'flex', justifyClass, alignClass, className)}
                {...props}
            />
        );
    }
);
Center.displayName = 'Center';

export { Container, Stack, Row, Grid, GridItem, Spacer, Divider, Center };
